import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, rupiahShort, formatDate } from "@/lib/format";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { CampaignLinks } from "@/components/talent/CampaignLinks";
import { ClickStatsChart, StatPill } from "@/components/ClickStatsChart";
import { toast } from "sonner";
import {
  Wallet, TrendingUp, ArrowDownToLine, Clock, LogOut, Send, ShoppingBag,
  Link2, Copy, Check, RefreshCw, Unlink, Sparkles, CheckCircle2,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

export default function TalentPortal() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sales, setSales] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [meta, setMeta] = useState({ banks: [], platforms: [] });
  const [open, setOpen] = useState(false);
  const [openBankDialog, setOpenBankDialog] = useState(false);
  const [amount, setAmount] = useState("");

  // TikTok link state
  const [openTikTokModal, setOpenTikTokModal] = useState(false);
  const [tiktokHandle, setTiktokHandle] = useState("");
  const [tiktokCreatorId, setTiktokCreatorId] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [clickStats, setClickStats] = useState(null);

  const load = () => {
    api.get("/talents/me").then((res) => {
      setProfile(res.data);
      if (res.data.tiktok_handle) setTiktokHandle(res.data.tiktok_handle);
      if (res.data.tiktok_creator_id) setTiktokCreatorId(res.data.tiktok_creator_id);
    }).catch(() => {});
    api.get("/sales").then((res) => setSales(res.data)).catch(() => {});
    api.get("/payouts").then((res) => setPayouts(res.data)).catch(() => {});
    api.get("/talents/me/click-stats").then((res) => setClickStats(res.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const requestPayout = async () => {
    if (!amount) { toast.error("Isi jumlah penarikan"); return; }
    try {
      await api.post("/payouts/request", { amount: Number(amount), note: "Permintaan dari talent" });
      toast.success("Permintaan penarikan terkirim, menunggu diproses admin");
      setOpen(false);
      setAmount("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLinkTikTok = async () => {
    if (!tiktokHandle) {
      toast.error("Masukkan username TikTok Anda (misal @budi.kreator)");
      return;
    }
    try {
      const { data } = await api.post("/talents/me/tiktok-link", {
        tiktok_handle: tiktokHandle,
        tiktok_creator_id: tiktokCreatorId,
      });
      setProfile(data);
      setOpenTikTokModal(false);
      toast.success(`Akun TikTok ${data.tiktok_handle} berhasil terhubung dengan Komisia!`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleSyncTikTok = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/talents/me/tiktok-sync");
      toast.success(`Sinkronisasi berhasil! 1 order baru terdeteksi (${data.new_sale.product_name}) — Komisi +${rupiah(data.new_sale.talent_commission)}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const { data } = await api.post("/talents/me/tiktok-disconnect");
      setProfile(data);
      toast.success("Tautan akun TikTok Shop dilepas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const affiliateLink = profile?.affiliate_code
    ? `${window.location.origin}/ref/${profile.affiliate_code}?platform=tiktok_shop`
    : null;

  const copyAffiliateLink = () => {
    navigator.clipboard.writeText(affiliateLink);
    setCopied(true);
    toast.success("Link afiliasi disalin ke clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const bal = profile?.balance || {};
  const isTikTokConnected = profile?.tiktok_status === "connected";

  const trend = (() => {
    const m = {};
    sales.forEach((s) => {
      const key = (s.date || s.created_at || "").slice(0, 7);
      m[key] = (m[key] || 0) + (s.talent_commission || 0);
    });
    return Object.entries(m).sort().map(([month, commission]) => ({ month, commission }));
  })();

  return (
    <div className="min-h-screen" data-testid="talent-portal-container">
      <header className="border-b border-border sticky top-0 bg-background/80 backdrop-blur z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Komisia" className="h-9 w-9 rounded-lg" />
            <div>
              <p className="font-display font-bold text-sm leading-none">Komisia Talent</p>
              <p className="text-[10px] text-muted-foreground mt-1">Portal Afiliasi</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{profile?.tier} • {profile?.platform}</p>
            </div>
            <img src={profile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "T")}&background=6366F1&color=fff`} className="h-9 w-9 rounded-full object-cover border border-border" alt="" />
            <button data-testid="talent-logout-button" onClick={logout} className="text-muted-foreground hover:text-destructive p-2">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8 grid-glow">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Halo, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-muted-foreground mt-1">Ringkasan komisi & integrasi afiliasi Anda</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              data-testid="sync-tiktok-btn"
              onClick={handleSyncTikTok}
              disabled={syncing || !isTikTokConnected}
              variant="outline"
              className="border-border hover:bg-card text-foreground"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin text-primary" : "text-cyan-400"}`} />
              {syncing ? "Menyinkronkan..." : "Sinkronkan TikTok Shop"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="talent-request-payout-button" className="bg-primary hover:bg-primary/90 text-white">
                  <ArrowDownToLine className="h-4 w-4 mr-2" /> Tarik Komisi
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Penarikan Komisi</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="bg-background rounded-lg p-4 text-center">
                    <p className="text-xs text-muted-foreground">Saldo Tersedia</p>
                    <p className="font-display text-2xl font-bold text-emerald-400">{rupiah(bal.available)}</p>
                  </div>
                  <div>
                    <Label>Jumlah Penarikan (Rp)</Label>
                    <Input data-testid="talent-payout-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" />
                  </div>
                  <div className="text-xs text-muted-foreground bg-background rounded-lg p-3">
                    Dana dikirim ke <span className="font-mono text-foreground">{profile?.bank_name} • {profile?.account_number}</span> via Easylink.
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="talent-payout-submit-button" onClick={requestPayout} className="w-full bg-primary hover:bg-primary/90 text-white">
                    <Send className="h-4 w-4 mr-2" /> Ajukan Penarikan
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* TIKTOK SHOP LINK & AFFILIATE CARD */}
        <div className="bg-gradient-to-r from-card via-[#121c2e] to-card border border-border rounded-2xl p-6 shadow-xl relative overflow-hidden" data-testid="tiktok-integration-card">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="space-y-2 max-w-xl">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-md bg-[#00f2fe]/10 text-[#00f2fe] border border-[#00f2fe]/20 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> Integrasi TikTok Shop
                </span>
                {isTikTokConnected ? (
                  <span className="text-xs px-2.5 py-1 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Terhubung & Aktif
                  </span>
                ) : (
                  <span className="text-xs px-2.5 py-1 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/30">
                    Belum Terhubung
                  </span>
                )}
              </div>
              <h2 className="font-display text-xl font-bold">
                {isTikTokConnected ? `Akun Terhubung: ${profile?.tiktok_handle || "@kreator"}` : "Hubungkan Akun TikTok Shop Anda"}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {isTikTokConnected
                  ? `ID Kreator: ${profile?.tiktok_creator_id || "-"}. Penjualan dari keranjang kuning video & live otomatis tersinkron ke saldo Komisia Anda.`
                  : "Tautkan username TikTok Anda untuk menyinkronkan data penjualan keranjang kuning & dapatkan link referral afiliasi otomatis."}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {isTikTokConnected ? (
                <>
                  <Button
                    data-testid="open-tiktok-modal-btn"
                    onClick={() => setOpenTikTokModal(true)}
                    variant="outline"
                    className="border-border hover:bg-background text-sm"
                  >
                    Ubah Akun
                  </Button>
                  <Button
                    data-testid="disconnect-tiktok-btn"
                    onClick={handleDisconnect}
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive text-sm"
                  >
                    <Unlink className="h-4 w-4 mr-1.5" /> Putuskan
                  </Button>
                </>
              ) : (
                <Button
                  data-testid="connect-tiktok-btn"
                  onClick={() => setOpenTikTokModal(true)}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg shadow-primary/25"
                >
                  <Link2 className="h-4 w-4 mr-2" /> Hubungkan TikTok Shop
                </Button>
              )}
            </div>
          </div>

          {/* AFFILIATE LINK BAR */}
          <div className="mt-6 pt-5 border-t border-border/70 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-primary shrink-0" />
              <span className="text-xs text-muted-foreground font-medium">Link Afiliasi Unik Anda:</span>
            </div>
            <div className="flex items-center gap-2 flex-1 max-w-lg">
              <div className="bg-background/80 border border-border rounded-lg px-3 py-2 text-xs font-mono text-cyan-400 truncate flex-1">
                {affiliateLink || "Hubungkan TikTok Shop untuk mendapatkan link afiliasi"}
              </div>
              <Button
                size="sm"
                data-testid="copy-affiliate-link-btn"
                onClick={copyAffiliateLink}
                disabled={!affiliateLink}
                className="bg-primary hover:bg-primary/90 text-white shrink-0 disabled:opacity-40"
              >
                {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                {copied ? "Disalin!" : "Salin Link"}
              </Button>
            </div>
          </div>
        </div>

        {/* DIALOG LINK TIKTOK */}
        <Dialog open={openTikTokModal} onOpenChange={setOpenTikTokModal}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-cyan-400" /> Tautkan Akun TikTok Shop
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2 text-sm">
              <div className="bg-background rounded-lg p-3 space-y-1 text-xs text-muted-foreground">
                <p className="font-semibold text-foreground">📌 3 Langkah Mudah:</p>
                <p>1. Masukkan Username TikTok Anda di bawah.</p>
                <p>2. Buka aplikasi TikTok & konfirmasi undangan agency dari <b>Komisia</b> di Inbox.</p>
                <p>3. Semua penjualan keranjang kuning otomatis masuk ke saldo Anda!</p>
              </div>
              <div>
                <Label>Username TikTok</Label>
                <Input
                  data-testid="tiktok-handle-input"
                  value={tiktokHandle}
                  onChange={(e) => setTiktokHandle(e.target.value)}
                  placeholder="@username.kreator"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div>
                <Label>TikTok Creator ID (Opsional)</Label>
                <Input
                  data-testid="tiktok-creator-id-input"
                  value={tiktokCreatorId}
                  onChange={(e) => setTiktokCreatorId(e.target.value)}
                  placeholder="Contoh: TT-CREATOR-8921"
                  className="mt-1.5 font-mono"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="submit-tiktok-link-btn"
                onClick={handleLinkTikTok}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
              >
                Simpan & Hubungkan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-card border border-border rounded-xl p-5" data-testid="talent-click-stats-card">
            <h3 className="font-semibold mb-3">Statistik Klik & Konversi</h3>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <StatPill testid="talent-total-clicks" label="Klik" value={clickStats?.total_clicks ?? 0} accent="#06B6D4" />
              <StatPill testid="talent-total-orders" label="Order" value={clickStats?.total_orders ?? 0} accent="#F59E0B" />
              <StatPill testid="talent-conversion" label="Konversi" value={`${clickStats?.conversion_pct ?? 0}%`} accent="#10B981" />
            </div>
            <p className="text-[11px] text-muted-foreground mb-1">Klik 14 hari terakhir</p>
            <ClickStatsChart data={clickStats?.daily} height={160} />
          </div>
          <div className="lg:col-span-2">
            <CampaignLinks affiliateCode={isTikTokConnected ? profile?.affiliate_code : null} links={clickStats?.links || []} onChange={load} />
          </div>
        </div>

        {/* BALANCE CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: TrendingUp, label: "Total Komisi", value: bal.total_earned, accent: "#F59E0B", testid: "talent-total-earned" },
            { icon: Wallet, label: "Tersedia Ditarik", value: bal.available, accent: "#10B981", testid: "talent-available" },
            { icon: Clock, label: "Dalam Proses", value: bal.locked, accent: "#06B6D4", testid: "talent-locked" },
            { icon: ArrowDownToLine, label: "Total Ditarik", value: bal.withdrawn, accent: "#6366F1", testid: "talent-withdrawn" },
          ].map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              data-testid={c.testid}
              className="bg-card border border-border rounded-xl p-5 relative overflow-hidden"
            >
              <div className="h-10 w-10 rounded-lg grid place-items-center mb-3" style={{ background: `${c.accent}22` }}>
                <c.icon className="h-5 w-5" style={{ color: c.accent }} />
              </div>
              <p className="text-xs text-muted-foreground">{c.label}</p>
              <p className="font-display text-xl font-bold mt-1 font-mono">{rupiah(c.value)}</p>
            </motion.div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4">Tren Komisi Bulanan</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="gTal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="month" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} tickFormatter={rupiahShort} width={70} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
              <Area type="monotone" dataKey="commission" stroke="#F59E0B" fill="url(#gTal)" strokeWidth={2} name="Komisi" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><ShoppingBag className="h-4 w-4 text-cyan-400" /> Penjualan Afiliasi Terbaru</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="talent-sales-list">
              {sales.slice(0, 12).map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-background rounded-lg p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.product_name}</p>
                    <p className="text-xs text-muted-foreground">{s.platform} • {formatDate(s.date)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono text-amber-400">{rupiah(s.talent_commission)}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">GMV {rupiahShort(s.gmv)}</p>
                  </div>
                </div>
              ))}
              {sales.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Belum ada penjualan.</p>}
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Wallet className="h-4 w-4 text-emerald-400" /> Riwayat Penarikan</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="talent-payout-history">
              {payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between bg-background rounded-lg p-3">
                  <div>
                    <p className="text-sm font-mono">{rupiah(p.amount)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(p.created_at)} • {p.tarapay_ref || p.easylink_ref || "menunggu"}</p>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
              ))}
              {payouts.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Belum ada penarikan.</p>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
